import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { inject, Injectable } from '@angular/core';
import { AbstractListModel } from '@libs/factory/abstract-list/abstract-list.model';
import { PushService } from '@core/push/push.service';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { AgentService } from '@api/services/agent.service';
import { AddAgentPaymentRequest } from '@api/models/AgentApi/Contract/add-agent-payment-request';
import { startWithLoading } from '@core/state/operators/start-with-loading';
import { PushCodeModel } from '@core/push/push-code.model';
import { AgentPayments } from '@api/models/AgentApi/Contract/agent-payments';
import { SetAgentPaymentStatusRequest } from '@api/models/AgentApi/Contract/set-agent-payment-status-request';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { CustomRxState } from '@core/state/rx.state';


export interface FundsTransfersBodyModel {
    status?: number;
    start: string;
    end: string;
}

interface IFundsTransfersState {
    loading: Record<StartWithUnit, boolean>;
    fundsTransfers: AgentPayments[];
    fundsTransfersFilter: FundsTransfersBodyModel;
    statuses: AbstractListModel[];
}

interface FundsTransfersActions {
    requestFundsTransfers: AgentPayments[];
    setFundsTransfersFilter: FundsTransfersBodyModel;
}

@Injectable()
export class FundsTransfersState extends CustomRxState<IFundsTransfersState> {
    readonly #push: PushService = inject(PushService);
    readonly #translate: TranslateService = inject(TranslateService);
    readonly #agentService: AgentService = inject(AgentService);
    readonly #actions: RxActions<FundsTransfersActions> = new RxActionFactory<FundsTransfersActions>().create();

    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');
    public readonly fundsTransfers$: Observable<AgentPayments[]> = this.select('fundsTransfers');
    public readonly fundsTransfersFilter$: Observable<FundsTransfersBodyModel> = this.select('fundsTransfersFilter');
    public readonly statuses$: Observable<AbstractListModel[]> = this.select('statuses').pipe(
        switchMap((statuses: AbstractListModel[]) =>
            this.#translate.get(marker('global.labels.any')).pipe(
                map(() =>
                    statuses.map((item: AbstractListModel) => ({
                        ...item,
                        title: this.#translate.instant(item.title),
                    })),
                ),
            )),
    );

    constructor() {
        super();

        this.setDefaultState();
        this.connectSelectors();
    }

    public createTransfer(body: AddAgentPaymentRequest): Observable<void> {
        return this.#agentService.apiAgentAddAgentPaymentPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.RequestAddFundTransfer),
            tap(() => this.#push.success(PushCodeModel.SuccessAdd)),
        );
    }

    public setStatusTransfer(body: SetAgentPaymentStatusRequest[]): Observable<void> {
        return this.#agentService.apiAgentSetAgentPaymentStatusPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.RequestSetStatusFundTransfer),
            tap(() => this.#push.success(PushCodeModel.SuccessChangeStatus)),
        );
    }

    public requestFundsTransfers(body: FundsTransfersBodyModel): void {
        this.hold(
            this.#agentService.apiAgentListAgentPaymentsGet(body).pipe(
                startWithLoading(this, StartWithUnit.RequestFundsTransfers),
            ),
            this.#actions.requestFundsTransfers,
        );
    }

    public setFundsTransfersFilter(body: FundsTransfersBodyModel): void {
        this.#actions.setFundsTransfersFilter(body);
    }

    private setDefaultState(): void {
        this.set({
            loading: null,
            statuses: [
                {
                    id: -1,
                    title: marker('global.labels.any'),
                },
                {
                    id: 0,
                    title: marker('agents.transferFundOrderStatus.wait'),
                },
                {
                    id: 1,
                    title: marker('agents.transferFundOrderStatus.approved'),
                },
                {
                    id: 2,
                    title: marker('agents.transferFundOrderStatus.reject'),
                },
            ],
        });
    }

    private connectSelectors(): void {
        this.connect('fundsTransfers', this.#actions.requestFundsTransfers$);
        this.connect('fundsTransfersFilter', this.#actions.setFundsTransfersFilter$);

    }
}
